import { Component, EventEmitter, Input, OnInit, Optional, Output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Credentials } from '../../../models/credentials.model';
import { ConnectService } from '../../../services/connect.service';
import { AbstractSigaleComponent } from '../../abstract-sigale.component';
import { BoutonSsoAzureComponent } from '../../../modules/sso/bouton-sso-azure/bouton-sso-azure.component';
import { FieldsetModule } from 'primeng/fieldset';
import { animate, style, transition, trigger } from '@angular/animations';
import { SsoConfigurationService } from '../../../modules/sso/sso-configuration.service';

@Component({
	standalone: true,
	selector: 'login',
	templateUrl: './login.html',
	styleUrls: ['./login.scss'],
	imports: [
		CommonModule,
		FormsModule,
		FieldsetModule,
		TranslateModule,
		BoutonSsoAzureComponent
	],
	animations: [
		trigger('slideFade', [
			transition(':enter', [
				style({ opacity: 0, transform: 'translateY(-10px)' }),
				animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
			]),
			transition(':leave', [
				animate('150ms ease-in', style({ opacity: 1, transform: 'translateY(-10px)' }))
			])
		])
	]
})
export class LoginComponent extends AbstractSigaleComponent implements OnInit{

	/**
	 * Indique si l'on est dans le cadre d'une déconnexion forcée par l'administrateur et qu'il s'agit pour l'utilisateur de redonner son mot de passe pour rafraichir son JWT
	 */
	@Input() rafraichirLogin = false;

	/**
	 * Les données de connexion, afin d'afficher l'identifiant et griser le champ si on est en mode reconnexion.
	 */
	@Input() loginData: Credentials = new Credentials();

	/**
	 * Si l'utilisateur ne peut pas modifier son login, on grise le champ identifiant.
	 */
	@Input() peutModifierLogin: boolean = true;

	@Output() loginEnSucces = new EventEmitter<boolean>();
	message: string;

	/**
	 * Rend la connexion via SSO obligatoire si true.
	 */
	estSsoOnly: boolean = false;

	/**
	 * Ajoute le terme SIGALE devant le nom de l'application si true.
	 */
	estApplicationSigale: boolean = true;

	autresOptionsPossiblesVisible: boolean;

	constructor(
		private connectService: ConnectService,
		@Optional() private ssoConfigurationService: SsoConfigurationService) {
		super();
		this.translateService.setDefaultLang('FR');
		this.translateService.currentLang = 'FR';
		// Si le sso est inactif on doit afficher par défaut la connexion classique
		this.autresOptionsPossiblesVisible = !this.ssoConfigurationService?.ssoActif();
	}

	ngOnInit() {
		super.ngOnInit();
		this.connectService.recupererConfigurationPourFormulaireConnexion()
			.subscribe({
				next: configurationDto => {
					this.estSsoOnly = configurationDto.estSsoOnly;
					this.estApplicationSigale = configurationDto.estApplicationSigale;
				}
			});
	}

	/**
	 * Effectue la connexion ou rafraichit le token
	 */
	login() {
		this.message = undefined;
		if (this.rafraichirLogin) {
			this.connectService.rafraichirToken(this.loginData, (data, status, headers) => this.onLoginSucces(data, status, headers), (reponse) => this.onLoginErreur(reponse));
		}
		else {
			this.connectService.login(this.loginData, (reponse) => this.onLoginErreur(reponse));
		}
	}

	/**
	 * Callback appelé en cas de connexion réussie.
	 *
	 * @param data le corps de la réponse.
	 * @param status le statut de la réponse.
	 * @param headers les en-têtes de la réponse.
	 */
	onLoginSucces(data, status, headers): void {
		// Pour les applications hybrides, on set le token dans le localStorage.
		// TODO : à supprimer quand toutes les applications seront en full angular.
		localStorage.setItem('access_token', headers.get('authorization'));

		this.loginEnSucces.emit(true);
	}

	/**
	 * Callback appelé suite à une connexion via SSO.
	 */
	onLoginSSOSucces(): void {
		this.loginEnSucces.emit(true);
	}

	/**
	 * Callback appelé en cas de connexion échouée.
	 *
	 * @param reponse la réponse.
	 */
	onLoginErreur(reponse): void {
		if (reponse.status === 504 || reponse.status === 404) {
			this.message = 'L\'application n\'a pas répondue. Veuillez contacter la hotline.';
		}
		else {
			this.message = JSON.parse(reponse.error).message;
		}
	}

	traductionFR(): any {
		return {
			'SIGALE_ANGULAR': {
				'COMPONENTS': {
					'LOGIN': {
						'CONNEXION': 'Connexion',
						'FIELDSET': 'Veuillez vous identifier',
						'IDENTIFIANT': 'Identifiant',
						'PASSWORD': 'Mot de passe'
					}
				}
			}
		};
	}

	traductionIT(): any {
		return {
			'SIGALE_ANGULAR': {
				'COMPONENTS': {
					'LOGIN': {
						'CONNEXION': 'Accedi',
						'FIELDSET': 'Si prega di identificarsi',
						'IDENTIFIANT': 'Nome utente',
						'PASSWORD': 'Password'
					}
				}
			}
		};
	}
}
